/**
 * Memory mount judge (N-182 P3 · proposal R1.3 / R1.4) — the SINGLE place that
 * decides, for one turn on any entry (desktop chat, desktop background task,
 * server conversation turn, server Agent task turn), whether the employee's
 * long-term memory is read and where new notes are written.
 *
 * Business (product voice): the same employee talking to the same Agent on the
 * desktop, in Feishu or in a cloud Agent task uses ONE long-term memory. The
 * server entries read the employee's own memory snapshot and write only into a
 * staging area that the nightly consolidation folds in; a turn whose answer
 * other group members will see mounts nobody's personal memory.
 *
 * `resolveMemoryMode` (the desktop three-state judge) lives here so this judge can
 * call it (dependency direction is `shared ← core`). Facts: docs/system/design/memory.md;
 * the R-numbers in comments across this feature refer to the original proposal in
 * PR leave1206/dosia#4470 (`docs/system/proposal/01.跨入口长期记忆.md`).
 */
/**
 * Resolve a session's three-state memory mode — the SINGLE source every consumer
 * (env injection / sandbox tier / allow-rule / recall selector) reads through.
 *
 * PERSONAL (unchanged semantics — never produces "write-only"):
 *   memoryEnabled===false → off ; chat.memoryDisabled → off ; else on.
 *   (lean-profile fold flips to "off" later in applySessionProfile.)
 *
 * ENTERPRISE (P5-A 员工记忆治理，用户拍板口径: 员工只留一个「记忆读取」开关；写入恒开):
 *   orgMemoryEnabled===false → off ;
 *   else if memoryReadToggleAllowed===false → on（开关未授予员工，
 *     员工存储里可能残留旧的 memoryReadEnabled=false —— 不再采信，读取跟随
 *     org 默认开。这是 2026-07-10 的治理修复：此前企业分支无条件采信
 *     memoryReadEnabled，导致「员工先自关读取 → admin 后收权」的时序下员工
 *     永久卡在 write-only 且 UI 已隐藏开关、无法自行改回）；
 *   else 写恒开 → memoryReadEnabled!==false ? on : write-only。
 *   企业分支明确 BYPASS 个人 memoryEnabled / memoryDirectory / chat.memoryDisabled
 *   —— 这些是个人版语义，企业下由 admin 治理，员工无权借它们改写记忆行为。
 *   (lean-profile fold: profile.memory!==true → off in applySessionProfile,
 *    后台任务不读不写个人记忆，维持现语义。)
 */
export function resolveMemoryMode(input) {
    if (input.deployMode === "enterprise") {
        if (input.orgMemoryEnabled === false)
            return "off";
        // Toggle not granted to the employee → their stored memoryReadEnabled (even
        // a stale `false` from before admin revoked it) no longer applies; read
        // follows the org default (on). We deliberately do NOT reset/clear the
        // employee's stored memoryReadEnabled here — the preference is preserved
        // and takes effect again automatically if the admin re-grants the toggle.
        if (input.memoryReadToggleAllowed === false)
            return "on";
        // Write is always on for enterprise; the read toggle picks on vs write-only.
        return input.memoryReadEnabled !== false ? "on" : "write-only";
    }
    // Personal — bypasses enterprise governance inputs; semantics = pre-P5-A.
    if (input.personalMemoryEnabled === false)
        return "off";
    if (input.chatMemoryDisabled)
        return "off";
    return "on";
}
/** Which entry a turn comes from. `server-task` is ONLY the cloud Agent turn
 *  (`agentTurn` on agent-host); the script and single-model-call tiers never
 *  go through this judge and never touch memory (proposal R1.7). */
export const MEMORY_MOUNT_ENTRIES = [
    "desktop-chat",
    "desktop-background",
    "server-conversation",
    "server-task",
];
const MEMORY_MOUNT_ENTRY_SET = new Set(MEMORY_MOUNT_ENTRIES);
export function isMemoryMountEntry(value) {
    return typeof value === "string" && MEMORY_MOUNT_ENTRY_SET.has(value);
}
const NO_MEMORY = { read: "none", write: "none" };
function isServerEntry(entry) {
    return entry === "server-conversation" || entry === "server-task";
}
/**
 * Proposal R1.4, first match wins:
 *   1. org memory off                                   → none / none (inside resolveMemoryMode)
 *   2. a profile is present and its `memory !== true`   → none / none (lean fold)
 *   3. answer visible to the group                      → none / none (server)
 *   4. turn employee ≠ session owner, or either empty   → none / none (server)
 *   5. server entry without employee isolation          → none / none (server)
 *   6. read: off when the granted read toggle is off, else personal
 *   7. write: desktop-chat main/staging as today; server-conversation staging;
 *      server-task none (read only); desktop-background follows the same fold.
 * Rules 3–5 guard the server entries; a desktop turn runs on the employee's own
 * device, where the answer is theirs and the session is theirs by construction.
 */
export function resolveMemoryMount(input) {
    if (input.profile !== undefined && input.profile.memory !== true)
        return NO_MEMORY;
    const server = isServerEntry(input.entry);
    if (server) {
        if (input.mode.deployMode !== "enterprise")
            return NO_MEMORY;
        if (input.audience === "group-visible")
            return NO_MEMORY;
        const owner = input.sessionOwner?.trim();
        const employee = input.turnEmployee?.trim();
        if (!owner || !employee || owner !== employee)
            return NO_MEMORY;
        if (input.isolationActive !== true)
            return NO_MEMORY;
    }
    const mode = resolveMemoryMode(input.mode);
    if (mode === "off")
        return NO_MEMORY;
    const read = mode === "on" ? "personal" : "none";
    if (server) {
        return { read, write: input.entry === "server-task" ? "none" : "staging" };
    }
    return { read, write: mode === "on" ? "main" : "staging" };
}
//# sourceMappingURL=memory-mount.js.map